<template>
    <div id="general-ledger-big" class="w-full h-full flex flex-col p-3">
        <div class="flex items-center">
            <transaction-filter enable-date-filter enable-search-filter @update="onSearchUpdated" />
            <UButton size="sm" class="min-w-fit" @click="exportCSV">Eksporter CSV</UButton>
        </div>
        <div>
            <tag
                v-if="accountNo"
                :text="'Konto: ' + accountNo"
                removable
                @remove="removeAccountNo"
            />
        </div>
        <loading-bar :style="{ visibility: loading ? 'visible' : 'hidden' }" data-cy="loading-indicator" />
        <div class="flex flex-col gap-2 overflow-y-auto bg-base-200 ">
            <div :style="{height: calcLedgerHeight(ledger?.ledgerAccounts || [])+'px'}" class="py-1.5">
                <gen-leg-flat-account-grid :ledger-accounts="ledger?.ledgerAccounts || []" :search-params="searchParams" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
//@ts-ignore
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import TransactionFilter from '@/transaction/TransactionFilter.vue'
import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {clamp, noop} from 'lodash'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED} from '@/types/events'
import {usePanelManager} from '@tritt/multipanel'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import LoadingBar from '@/components/LoadingBar.vue'
import {getGeneralLedger} from '@/genleg/ledger.service'
import {Ledger} from '@/types/reports/Ledger'
import {GENERAL_LEDGER} from '@/genleg'
import {Ref} from 'vue/dist/vue'
import GenLegFlatAccountGrid from '@/genleg/GenLegFlatAccountGrid.vue'
import {inject, onMounted, onUnmounted, ref, watch} from 'vue'
import Tag from '@/dimensions/Tag.vue'
import {createGeneralLedgerCSVExport} from '@/services/reports/summaryReport.service'
import {download} from '@/services/infrastructure/storage.service'
import {PanelContentRequest} from '@tritt/multipanel'

const searchParams = ref<TransactionSearchParams>(defaultSearchParams())
const ledger = ref<Ledger>()
const panelId: Ref<number> | undefined = inject('panelId')

const { onEvent, cleanUp } = useEmitter()
const {setPanelPath} = usePanelManager()
const loading = ref(false)

const props = defineProps({
    accountNo: {
        type: String,
        required: false
    }
})

// @ts-ignore
window.pdfExport = () => {
    if (!ledger.value) {
        return console.error('Ledger is not loaded')
    }
}

const removeAccountNo = () => {
    searchParams.value.accountNo = undefined
    clearData()
    fetchLedger(searchParams.value)
}

const calcLedgerHeight = (ledgerAccounts: Array<LedgerAccount>) => {
    let numTransactionLines = 0
    ledgerAccounts.forEach(ledgerAccount => {
        numTransactionLines += ledgerAccount.numTransactionLines
    })
    const cappedlineCount = clamp(numTransactionLines, 0, 50)
    return cappedlineCount * 28 + 115
}

const onSearchUpdated = (updatedSearchParams: TransactionSearchParams) => {
    searchParams.value = updatedSearchParams
    clearData()
    fetchLedger(updatedSearchParams)
}

const clearData = () => {
    ledger.value = undefined
}

const fetchLedger = async (searchParams: TransactionSearchParams) => {
    if (!searchParams.fromDate || !searchParams.toDate) return
    ledger.value = await getGeneralLedger({
        fromDate: searchParams.fromDate,
        toDate: searchParams.toDate,
        accountNo: searchParams.accountNo,
        project: searchParams.project,
        department: searchParams.department,
        fullText: searchParams.query,
    })

    setTimeout(() => {
        loading.value = false
    }, 100)
}

onMounted(async () => {
    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the ledger page without selected org. Should not be possible, this is a bug!')
    }

    if (props.accountNo) {
        searchParams.value.accountNo = props.accountNo
    }

    loading.value = true
    onEvent(TRANSACTION_CREATED, async () => {
        clearData()
        await fetchLedger(searchParams.value)
    })
    onEvent(TRANSACTION_UPDATED, () => {
        clearData()
        fetchLedger(searchParams.value)
    })
    onEvent(DELETE_TRANSACTION, () => {
        clearData()
        fetchLedger(searchParams.value)
    })
    onEvent(ORG_SELECTED, () => {
        clearData()
        fetchLedger(searchParams.value)
    })
})

watch(() => props.accountNo, (newAccountNo) => {
    if (newAccountNo) {
        searchParams.value.accountNo = newAccountNo
        clearData()
        fetchLedger(searchParams.value)
    }
})

watch(() => searchParams.value.accountNo, () => {
    if (searchParams.value.accountNo !== props.accountNo) {
        const payload: PanelContentRequest = {
            componentName: GENERAL_LEDGER.componentName,
            queryValues: {},
            panelId: panelId?.value
        }

        setPanelPath(payload)
    }
})

const exportCSV = async () => {
    if (searchParams.value.toDate && searchParams.value.fromDate) {
        const generalLedgerCsv = await createGeneralLedgerCSVExport(searchParams.value)
        if (generalLedgerCsv) {
            download(generalLedgerCsv).then(noop)
        }
    }
}

onUnmounted(cleanUp)
</script>

<style>
@reference "@/index.css";
@reference "tailwindcss";

#general-ledger {
    .ag-header {
        height: 0;
        border: 0;
    }
}
</style>